import { Box, Textarea, VStack } from "@chakra-ui/react"
import AugmentationPanelFormActions from "@pathwright/ux/src/components/augmentation/AugmentationPanelFormActions"
import {
  AUGMENTATION_PANEL_INPUT_PROPS,
  AUGMENTATION_PANEL_TEXTAREA_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { AvatarImagePicker } from "@pathwright/ux/src/components/avatar/AvatarImagePicker"
import Field from "@pathwright/ux/src/components/form/Field"
import {
  FieldTip,
  TippedField
} from "@pathwright/ux/src/components/form/TippedField"
import {
  FormikSubmissionState,
  FormikWithDiscardFormState
} from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import { DiscardFormStateReturn } from "@pathwright/ux/src/components/form/state/useDiscardFormState"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { Form, FormikProps } from "formik"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import { useUpdateUserMutation } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

const tPrefix = "profile.form"
const FORM_ID = "user-augmentation-settings"

const validationSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Invalid email address"),
  bio: z.string(),
  location: z.string(),
  avatar: z.string()
})

type Values = z.infer<typeof validationSchema>

const formikValidator = toFormikValidate(validationSchema)

type ProfileFormProps = FormikProps<Values> &
  FormikSubmissionState &
  DiscardFormStateReturn

function UserAugmentationSettings() {
  const { t } = useTranslate()
  const me = usePathwrightContext().me!
  const [updateUser] = useUpdateUserMutation()

  const initialValues: Values = {
    firstName: me.first_name,
    lastName: me.last_name,
    email: me.email || "",
    bio: me.profile?.bio || "",
    location: me.profile?.location || "",
    avatar: me.profile?.imageRaw || ""
  }

  async function handleSubmit(values: Values) {
    await updateUser({
      variables: {
        user: {
          id: me.id!,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email
        },
        profile: {
          id: me.profile?.id!,
          bio: values.bio,
          location: values.location,
          avatar: values.avatar
        }
      }
    })
  }

  const visbleToAllTip: FieldTip = {
    icon: "eye",
    title: t(`${tPrefix}.visibility_tip_all.Visible to all members.`)
  }

  const visibleToStaffTip: FieldTip = {
    icon: "eye-off",
    title: t(`${tPrefix}.visibility_tip_staff.Visible to you and staff only.`)
  }

  return (
    <FormikWithDiscardFormState
      formStateKey="profile"
      initialValues={initialValues}
      validate={formikValidator}
      onSubmit={handleSubmit}
      enableReinitialize
      // TODO: it seems the form state is reset on submit (just a "visiual" glitch
      // as the changes are properly saved), but we should fix this.
      formStateOnSubmit="preserve"
    >
      {(form) => {
        const profileForm = form as ProfileFormProps

        return (
          <Form
            id={FORM_ID}
            className="UserAugmentationSettings"
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <KeyboardFormSubmit />
            <VStack
              align="stretch"
              gap="augmentationPanel"
              p="augmentationPanel"
              w="full"
              css={{
                "& .chakra-input__right-addon .TooltipWrapper": {
                  opacity: 0.6
                }
              }}
            >
              <Box alignSelf="center">
                <AvatarImagePicker
                  avatar={profileForm.values.avatar}
                  firstName={profileForm.values.firstName}
                  lastName={profileForm.values.lastName}
                  fullName={[
                    profileForm.values.firstName,
                    profileForm.values.lastName
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  isDisabled={profileForm.isSubmitting}
                  onChange={(avatar) => {
                    void profileForm.setFieldValue("avatar", avatar)
                  }}
                />
              </Box>
              <TippedField
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="firstName"
                label={t(`${tPrefix}.first_name`)}
                placeholder={t(`${tPrefix}.first_name_ph`)}
                tip={visbleToAllTip}
              />
              <TippedField
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="lastName"
                label={t(`${tPrefix}.last_name`)}
                placeholder={t(`${tPrefix}.last_name_ph`)}
                tip={visibleToStaffTip}
              />
              <TippedField
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="email"
                label={t(`${tPrefix}.email`)}
                placeholder={t(`${tPrefix}.email_ph`)}
                tip={visibleToStaffTip}
              />
              <TippedField
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="location"
                label={t(`${tPrefix}.location`)}
                placeholder={t(`${tPrefix}.location_ph`)}
                tip={visbleToAllTip}
              />
              <Field
                {...AUGMENTATION_PANEL_TEXTAREA_PROPS}
                as={Textarea}
                name="bio"
                label={t(`${tPrefix}.about_you`)}
                placeholder={t(`${tPrefix}.about_you_ph`)}
                // Padding for visibility icon.
                pr="calc(2 * var(--input-padding, 8px))"
              >
                {({ field }) => (
                  <Box position="relative" w="100%">
                    {field}
                    <Tooltip title={visbleToAllTip.title} fitContent>
                      <Pathicon
                        icon={visbleToAllTip.icon}
                        size="1em"
                        pos="absolute"
                        // Top/right to match sizing of input padding used in InputGroup.
                        // (InputGroup doesn't seem to work with Textarea, and wouldn't expect it to.)
                        top="1rem"
                        right="1rem"
                        transform="translateY(-50%)"
                        opacity=".6"
                      />
                    </Tooltip>
                  </Box>
                )}
              </Field>
            </VStack>
            <AugmentationPanelFormActions
              form={profileForm}
              formId={FORM_ID}
              submit={{
                children: t(`${tPrefix}.save`)
              }}
              discard={{
                children: t(`${tPrefix}.discard`)
              }}
            />
          </Form>
        )
      }}
    </FormikWithDiscardFormState>
  )
}

export default UserAugmentationSettings
